import {
  type IndustrialCaseSummary,
  describeIndustrialCaseClassification,
} from '@/domain/industrialCase/industrialCaseSummary';
import Link from '@/routing/AppLink';
import Image from 'next/image';

import { IconArrowRightRound } from '@/components/apps/ui/icon';

import { buildIndustrialCasePath } from './industrialCaseRoutes';

type Props = {
  caseSummary: IndustrialCaseSummary;
  /** Announced on the link, since the visible label is only the title */
  readLabel: string;
  metricLabel: string;
  onSelect?: () => void;
};

/**
 * ### IndustrialCaseSummaryCard
 *
 * One published case as a card, used by the case list and by the related cases
 * at the end of a detail page.
 *
 * The whole card is one link rather than a card with a link inside it, so the
 * click target matches what the card looks like and keyboard focus lands once.
 * The hero image leads the card and the summary sentence is left to the detail
 * page, so a row of cards scans by picture, title, and figure.
 *
 * @param props The case to show, its labels, and an optional click reporter
 * @returns The case card
 */
export function IndustrialCaseSummaryCard({
  caseSummary,
  readLabel,
  metricLabel,
  onSelect,
}: Props) {
  const classification = describeIndustrialCaseClassification(caseSummary);

  return (
    <Link
      className="group flex h-full min-w-0 flex-col overflow-hidden border border-[#e2e2de] bg-apps-coreGrayWhite transition-colors duration-200 ease-out hover:border-apps-brandGrayBlack focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-apps-corePrimary01 motion-reduce:transition-none"
      href={buildIndustrialCasePath(caseSummary.slug)}
      prefetch={false}
      onClick={onSelect}
      aria-label={`${caseSummary.title} ${readLabel}`}
    >
      {/* 2:1 matches the case hero images, so the thumbnail shows the whole photo */}
      <div className="relative aspect-[2/1] w-full overflow-hidden border-b border-[#e2e2de] bg-[#f2f2ef]">
        {caseSummary.heroImage && (
          <Image
            className="object-cover transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.03] motion-reduce:transform-none motion-reduce:transition-none"
            src={caseSummary.heroImage}
            alt=""
            fill
            sizes="(min-width: 992px) 33vw, (min-width: 768px) 50vw, 100vw"
          />
        )}
        {/*
         * The product line, so Industrial and Safety cases stay apart in the
         * "all" list and among related cases. Each line has its own color, so
         * the two tell apart before the label is read; both clear 4.5:1 with
         * white text. Product names read the same in every language, so the
         * label is not translated.
         */}
        <span
          className={`absolute top-4 left-4 inline-flex items-center rounded-full px-3 py-1 text-[12px] font-semibold leading-[1.4] tracking-[0.01em] text-apps-coreGrayWhite shadow-[0_2px_8px_rgba(0,0,0,0.18)] ${
            caseSummary.solution === 'safety'
              ? 'bg-apps-corePrimary01'
              : 'bg-apps-semanticBlue600'
          }`}
        >
          {caseSummary.solution === 'safety' ? 'Safety' : 'Industrial'}
        </span>
      </div>

      <div className="flex min-w-0 flex-1 flex-col justify-between gap-6 px-6 pt-6 pb-7">
        <div className="min-w-0">
          {classification.length > 0 && (
            <p className="text-[13px] font-semibold leading-[1.5] text-[#777773]">
              {classification.join(' · ')}
            </p>
          )}
          <h3 className="mt-3 text-[26px] font-semibold leading-[1.25] tracking-[-0.03em] text-apps-brandGrayBlack text-balance">
            {caseSummary.title}
          </h3>
        </div>

        <div className="min-w-0">
          {caseSummary.primaryMetric.value && (
            <div className="border-t border-[#e8e8e5] pt-4">
              <strong className="block text-2xl font-semibold leading-[1.15] text-apps-brandGrayBlack [font-variant-numeric:tabular-nums]">
                {caseSummary.primaryMetric.value}
              </strong>
              <span className="mt-1 block text-[13px] font-medium leading-[1.5] text-[#6d6d69]">
                {caseSummary.primaryMetric.label || metricLabel}
              </span>
            </div>
          )}
          {/* 카드 전체가 링크라 버튼 요소를 겹치지 않고, 버튼처럼 보이는 표지로 둔다 */}
          <span className="mt-5 inline-flex min-h-[44px] items-center gap-1.5 rounded-full bg-apps-corePrimary01 px-5 text-sm font-semibold text-apps-coreGrayWhite transition-colors duration-200 group-hover:bg-apps-corePrimary02 motion-reduce:transition-none">
            {readLabel}
            <IconArrowRightRound
              className="transition-transform duration-250 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-x-1 motion-reduce:transform-none motion-reduce:transition-none"
              size={16}
              aria-hidden="true"
            />
          </span>
        </div>
      </div>
    </Link>
  );
}
